import { ArrowRight, CheckCircle2, Flag, Phone, SkipForward } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { CallLink } from "@/components/CallLink";
import { MicButton } from "@/components/MicButton";
import { errText } from "@/components/pipeline/utils";
import { mountInline, unmountInline } from "@/lib/ringoverPhone";
import "@/session.css";

const typing = (e) => ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName);
const nextTodo = (items, from) => { const i = items.findIndex((it, k) => k >= from && it.state === "todo"); return i >= 0 ? i : items.findIndex((it) => it.state === "todo"); };

const SessionList = ({ items, idx, onPick }) => (
  <ol className="cs-list" data-testid="session-list">
    {items.map((it) => (
      <li key={it.idx}><button type="button" className={`${it.idx === idx ? "on " : ""}${it.state}`} onClick={() => onPick(it.idx)} data-testid={`session-item-${it.idx}`}>
        <i />{it.title || it.phone}{it.tag ? <em>{it.tag}</em> : null}
      </button></li>
    ))}
  </ol>
);

export default function CallSessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [idx, setIdx] = useState(0);
  const [tag, setTag] = useState("");
  const [note, setNote] = useState("");
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/call-sessions/${id}`).then(({ data }) => { setS(data); setIdx(Math.max(nextTodo(data.items, data.position), 0)); }).catch((e) => toast.error(errText(e)));
    api.get("/call-sessions/quick-tags").then(({ data }) => setTags(data.tags)).catch(() => {});
  }, [id]);
  useEffect(() => {
    if (!s) return undefined;
    mountInline("cs-phone-frame").catch(() => {});
    return () => unmountInline();
  }, [Boolean(s)]); // eslint-disable-line react-hooks/exhaustive-deps
  const finish = useCallback(async () => {
    try { await api.post(`/call-sessions/${id}/end`); } catch { /* la session reste consultable */ }
    navigate(`/debrief?session=${id}`);
  }, [id, navigate]);
  const advance = useCallback(async (skip) => {
    if (busy || !s) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/call-sessions/${id}/items/${idx}/outcome`, { tag, note, skip });
      const items = s.items.map((it) => (it.idx === idx ? { ...it, state: skip ? "skipped" : "done", tag: skip ? it.tag : tag, debrief_id: data.debrief_id } : it));
      setS({ ...s, items });
      setTag(""); setNote("");
      const n = nextTodo(items, idx + 1);
      if (n < 0) { toast.success("Liste terminée, place au débrief."); finish(); } else setIdx(n);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  }, [busy, s, id, idx, tag, note, finish]);
  const called = () => { api.post(`/call-sessions/${id}/items/${idx}/called`).catch(() => {}); setS((v) => ({ ...v, items: v.items.map((it) => (it.idx === idx ? { ...it, called_at: new Date().toISOString() } : it)) })); };
  useEffect(() => {
    const onKey = (e) => {
      if (typing(e)) return;
      const n = Number(e.key);
      if (n >= 1 && n <= tags.length) { setTag((t) => (t === tags[n - 1] ? "" : tags[n - 1])); e.preventDefault(); }
      if (e.key === "Enter" || e.key === "ArrowRight") { advance(false); e.preventDefault(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tags, advance]);
  if (!s) return <section className="page" data-testid="call-session-page"><p className="pl-muted">Chargement de la session…</p></section>;
  const it = s.items[idx];
  const done = s.items.filter((x) => x.state !== "todo").length;
  return (
    <section className="page cs-page" data-testid="call-session-page">
      <header className="cs-head">
        <div><p className="eyebrow">SESSION D'APPELS{s.script_name ? ` · SCRIPT « ${s.script_name.toUpperCase()} »` : ""}</p><h1><Phone size={24} /> Mode rafale</h1></div>
        <button type="button" className="secondary-button" onClick={finish} data-testid="session-finish"><Flag size={15} /> Terminer et débriefer</button>
      </header>
      <div className="cs-progress" data-testid="session-progress"><span style={{ width: `${(done / s.items.length) * 100}%` }} /><b>{done} / {s.items.length} fiches</b></div>
      <div className="cs-grid">
        <article className="cs-card" key={it.idx} data-testid="session-current">
          <p className="cs-pos">Fiche {it.idx + 1} sur {s.items.length}{it.state !== "todo" ? ` · déjà ${it.state === "done" ? "appelée" : "passée"}` : ""}</p>
          <h2 data-testid="session-current-title">{it.title || "Sans nom"}</h2>
          <p className="pl-muted">{it.subtitle}</p>
          {it.lead_id && <Link className="pl-link" to={`/pipeline?lead=${it.lead_id}`} target="_blank" data-testid="session-open-lead">Ouvrir la fiche</Link>}
          <span className="cs-call" onClickCapture={called}><CallLink phone={it.phone} className={`cs-dial${it.called_at ? " called" : ""}`} testid="session-call-btn"><Phone size={18} /> {it.phone}</CallLink></span>
          <div className="cs-tags" data-testid="session-tags">
            {tags.map((t, i) => <button key={t} type="button" className={tag === t ? "on" : ""} onClick={() => setTag(tag === t ? "" : t)} data-testid={`session-tag-${i}`}><kbd>{i + 1}</kbd>{t}</button>)}
          </div>
          <div className="cs-note">
            <MicButton onText={(t) => setNote((n) => (n ? `${n} ${t}` : t))} testid="session-voice-note" label="Note vocale" />
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note rapide (facultative)…" rows={2} data-testid="session-note" />
          </div>
          <footer className="cs-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={() => advance(true)} data-testid="session-skip"><SkipForward size={15} /> Passer</button>
            <button type="button" className="primary-button" disabled={busy} onClick={() => advance(false)} data-testid="session-next">{busy ? "…" : <>Fiche suivante <ArrowRight size={15} /></>}</button>
          </footer>
          <p className="cs-hint"><CheckCircle2 size={13} /> Touches 1 à {tags.length} : tag · Entrée ou → : fiche suivante</p>
        </article>
        <aside className="cs-phone" data-testid="session-phone"><div id="cs-phone-frame" /></aside>
        <SessionList items={s.items} idx={idx} onPick={(i) => { setIdx(i); setTag(s.items[i].tag || ""); setNote(""); }} />
      </div>
    </section>
  );
}
